import { TUserAddress } from './types'
import UserActionTypes from './user-types'

type TUserState = {
    signInAuth: any
    currentUser: IIdentity
    canCreateCompany: any
    userAccounts: any
    error: any
    loading: any
    loader: any
    emailExists: any
    successData: any
    success: any
    latestSessions: any
    ipData: any
    onboardingFee: any
    enabledServices: any
    personalAccount: any
    userEmailStatus: any
    userPermissions: any
    userAddresses: TUserAddress[]
    signInAttemptExpired: any
    companiesRegistrationFee: any
    identityLimits: any
    ssAccessToken: any
    devices: any
    sumsubDocumentsSubmitted: boolean
}

const INITIAL_STATE: TUserState = {
    signInAuth: null,
    currentUser: null,
    canCreateCompany: null,
    userAccounts: null,
    error: null,
    loading: null,
    loader: {
        CHECK_EMAIL_AVAILABILITY: null,
    },
    emailExists: null,
    successData: null,
    success: null,
    latestSessions: null,
    ipData: null,
    onboardingFee: null,
    enabledServices: null,
    personalAccount: null,
    userEmailStatus: null,
    userPermissions: null,
    userAddresses: null,
    signInAttemptExpired: null,
    companiesRegistrationFee: null,
    identityLimits: null,

    ssAccessToken: null,
    devices: null,
    sumsubDocumentsSubmitted: false,
}

const userReducer = (state = INITIAL_STATE, action): TUserState => {
    switch (action.type) {
        case UserActionTypes.CHANGE_USER_STATUS:
            return {
                ...state,
                currentUser: {
                    ...state.currentUser,
                    refreshBrowser: true,
                    ...(!!action.payload?.accountDeactivated && { accountDeactivated: true }),
                },
            }

        case UserActionTypes.START_CHANGE_LANGUAGE:
            return {
                ...state,
                loader: { ...state.loader, CHANGE_LANGUAGE: true },
            }
        case UserActionTypes.SUCCESS_CHANGE_LANGUAGE:
            return {
                ...state,
                loader: { ...state.loader, CHANGE_LANGUAGE: null },
                currentUser: { ...state.currentUser, languageCode: action.payload },
            }
        case UserActionTypes.FAILURE_CHANGE_LANGUAGE:
            return {
                ...state,
                loader: { ...state.loader, CHANGE_LANGUAGE: null },
            }

        case UserActionTypes.FETCH_DEFAULT_2FA_TYPE:
            return {
                ...state,
                loader: { ...state.loader, DEFAULT_2FA_TYPE: true },
            }
        case UserActionTypes.SUCCESS_DEFAULT_2FA_TYPE:
            return {
                ...state,
                loader: { ...state.loader, DEFAULT_2FA_TYPE: null },
                currentUser: { ...state.currentUser, defaultTwoFactorAuthenticationType: action.payload },
            }

        case UserActionTypes.SUCCESS_USER_PHONE_CONFIRMATION:
            return {
                ...state,
                currentUser: { ...state.currentUser, isPhoneConfirmed: action.payload.isPhoneConfirmed, phone: action.payload.phone },
            }

        case UserActionTypes.START_PASSWORD_RESET_REQUEST:
            return {
                ...state,
                loader: { ...state.loader, PASSWORD_RESET_REQUEST: true },
            }
        case UserActionTypes.SUCCESS_PASSWORD_RESET_REQUEST:
            return {
                ...state,
                loader: { ...state.loader, PASSWORD_RESET_REQUEST: null },
                success: { ...state.success, PASSWORD_RESET_REQUEST: true },
                error: null,
            }
        case UserActionTypes.FINISH_PASSWORD_RESET_REQUEST:
            return {
                ...state,
                loader: { ...state.loader, PASSWORD_RESET_REQUEST: null },
                success: { ...state.success, PASSWORD_RESET_REQUEST: null },
                error: null,
            }
        case UserActionTypes.FAILURE_PASSWORD_RESET_REQUEST:
            return {
                ...state,
                loader: { ...state.loader, PASSWORD_RESET_REQUEST: null },
                success: { ...state.success, PASSWORD_RESET_REQUEST: null },
                error: true,
            }

        case UserActionTypes.START_BUSINESS_REGISTRATION_SIGN_UP:
            return {
                ...state,
                loader: { ...state.loader, BUSINESS_REGISTRATION_SIGN_UP: true },
            }
        case UserActionTypes.SUCCESS_BUSINESS_REGISTRATION_SIGN_UP:
            return {
                ...state,
                loader: { ...state.loader, BUSINESS_REGISTRATION_SIGN_UP: null },
                error: null,
            }
        case UserActionTypes.FAILURE_BUSINESS_REGISTRATION_SIGN_UP:
            return {
                ...state,
                loader: { ...state.loader, BUSINESS_REGISTRATION_SIGN_UP: null },
                error: true,
            }

        case UserActionTypes.SIGN_UP_START:
            return {
                ...state,
                loader: { ...state.loader, SIGN_UP: true },
            }
        case UserActionTypes.SIGN_UP_FAILURE:
            return {
                ...state,
                loader: { ...state.loader, SIGN_UP: null },
                error: true,
            }

        case UserActionTypes.AUTHENTICATE_USER:
            return {
                ...state,
                loader: { ...state.loader, SIGN_IN: true },
            }

        case UserActionTypes.SIGN_IN_START:
            return {
                ...state,
                loader: { ...state.loader, SIGN_IN: true },
            }
        case UserActionTypes.SIGN_IN_SUCCESS:
            return {
                ...state,
                currentUser: action.payload,
                canCreateCompany: action.payload?.canCreateCompany,
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                enabledServices: enabledServiceTypes(action.payload),
                loader: { ...state.loader, SIGN_IN_2FA: null, SIGN_UP: null, SIGN_IN: null },
                error: null,
            }
        case UserActionTypes.SIGN_IN_FINISH:
            return {
                ...state,
                loader: { ...state.loader, SIGN_IN_2FA: null, SIGN_IN: null },
                signInAuth: null,
                error: null,
            }
        case UserActionTypes.SIGN_IN_FAILURE:
            return {
                ...state,
                loader: { ...state.loader, SIGN_IN_2FA: null, SIGN_IN: null },
                error: true,
            }

        case UserActionTypes.START_TEMPORARY_PASSWORD_CHANGE:
            return {
                ...state,
                loader: { ...state.loader, TEMPORARY_PASSWORD_CHANGE: true },
                success: { ...state.success, TEMPORARY_PASSWORD_CHANGE: null },
                error: null,
            }
        case UserActionTypes.SUCCESS_TEMPORARY_PASSWORD_CHANGE:
            return {
                ...state,
                loader: { ...state.loader, TEMPORARY_PASSWORD_CHANGE: null },
                success: { ...state.success, TEMPORARY_PASSWORD_CHANGE: true },
                error: null,
            }
        case UserActionTypes.FAILURE_TEMPORARY_PASSWORD_CHANGE:
            return {
                ...state,
                loader: { ...state.loader, SIGN_IN_2FA: null, TEMPORARY_PASSWORD_CHANGE: null },
                success: { ...state.success, TEMPORARY_PASSWORD_CHANGE: null },
                error: true,
            }
        case UserActionTypes.FINISH_TEMPORARY_PASSWORD_CHANGE:
            return {
                ...state,
                loader: { ...state.loader, TEMPORARY_PASSWORD_CHANGE: null },
                success: { ...state.success, TEMPORARY_PASSWORD_CHANGE: null },
                error: null,
            }

        case UserActionTypes.START_RESEND_CONFIRM_EMAIL:
            return {
                ...state,
                loader: { ...state.loader, RESEND_CONFIRM_EMAIL: true },
            }
        case UserActionTypes.SUCCESS_RESEND_CONFIRM_EMAIL:
            return {
                ...state,
                loader: { ...state.loader, RESEND_CONFIRM_EMAIL: null },
                success: { ...state.success, RESEND_CONFIRM_EMAIL: true },
                error: null,
            }
        case UserActionTypes.FINISH_RESEND_CONFIRM_EMAIL:
            return {
                ...state,
                loader: { ...state.loader, RESEND_CONFIRM_EMAIL: null },
                success: { ...state.success, RESEND_CONFIRM_EMAIL: null },
                error: null,
            }
        case UserActionTypes.FAILURE_RESEND_CONFIRM_EMAIL:
            return {
                ...state,
                loader: { ...state.loader, SIGN_IN_2FA: null, RESEND_CONFIRM_EMAIL: null },
                success: { ...state.success, RESEND_CONFIRM_EMAIL: null },
                error: true,
            }

        case UserActionTypes.SIGN_IN_AUTH_START:
            return {
                ...state,
                signInAuth: action.payload,
                error: null,
            }

        case UserActionTypes.SIGN_IN_2FA_START:
            return {
                ...state,
                loader: { ...state.loader, SIGN_IN_2FA: true },
            }

        case UserActionTypes.ON_FAILURE:
            return {
                ...state,
                error: action.payload,
                loading: null,
            }

        case UserActionTypes.SUCCESS_FETCH_USER_ACCOUNTS: {
            const personalProfile = action.payload.find(({ name }) => name === 'Personal') || {}
            const userAccountsSorted = action.payload.sort((a, b) => (a.id > b.id ? 1 : b.id > a.id ? -1 : 0))

            return {
                ...state,
                userAccounts: userAccountsSorted,
                personalAccount: personalProfile,
            }
        }
        case UserActionTypes.START_USER_ACCOUNTS_PERMISSIONS:
            return {
                ...state,
                userPermissions: null,
                loader: { ...state.loader, USER_ACCOUNTS_PERMISSIONS: true },
            }
        case UserActionTypes.SUCCESS_USER_ACCOUNTS_PERMISSIONS:
            return {
                ...state,
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                userPermissions: enabledUserPermissions(action.payload),
                loader: { ...state.loader, USER_ACCOUNTS_PERMISSIONS: null },
            }
        case UserActionTypes.FINISH_USER_ACCOUNTS_PERMISSIONS:
            return {
                ...state,
                userPermissions: null,
                loader: { ...state.loader, USER_ACCOUNTS_PERMISSIONS: null },
            }

        case UserActionTypes.FETCH_ONBOARDING_FEE:
            return {
                ...state,
                onboardingFee: null,
            }
        case UserActionTypes.SUCCESS_ONBOARDING_FEE:
            return {
                ...state,
                onboardingFee: action.payload,
                error: null,
            }
        case UserActionTypes.FAILURE_ONBOARDING_FEE:
            return {
                ...state,
                onboardingFee: null,
                error: true,
            }

        case UserActionTypes.START_FETCH_ACTIVE_SERVICE_PLAN:
            return {
                ...state,
                loader: { ...state.loader, FETCH_ACTIVE_SERVICE_PLAN: true },
            }
        case UserActionTypes.SUCCESS_FETCH_ACTIVE_SERVICE_PLAN:
            return {
                ...state,
                currentUser: { ...state.currentUser, ...action.payload },
                loader: { ...state.loader, FETCH_ACTIVE_SERVICE_PLAN: null },
            }

        case UserActionTypes.SIGN_IN_ATTEMPT_EXPIRED:
            return {
                ...state,
                signInAttemptExpired: action.payload,
            }
        case UserActionTypes.SUCCESS_FETCH_SS_ACCESS_TOKEN:
            return {
                ...state,
                ssAccessToken: action.payload,
            }

        case UserActionTypes.SUCCESS_FETCH_LATEST_SESSIONS:
            return {
                ...state,
                latestSessions: action.payload.items || [],
            }

        case UserActionTypes.START_FETCH_IP_DATA:
            return {
                ...state,
                loader: { ...state.loader, FETCH_IP_DATA: true },
            }
        case UserActionTypes.SUCCESS_FETCH_IP_DATA:
            return {
                ...state,
                ipData: action.payload,
                loader: { ...state.loader, FETCH_IP_DATA: null },
            }
        case UserActionTypes.FAILURE_FETCH_IP_DATA:
            return {
                ...state,
                loader: { ...state.loader, FETCH_IP_DATA: null },
            }

        case UserActionTypes.START_CHECK_USER_EMAIL_STATUS:
            return {
                ...state,
                loader: { ...state.loader, CHECK_USER_EMAIL_STATUS: null },
            }
        case UserActionTypes.SUCCESS_CHECK_USER_EMAIL_STATUS:
            return {
                ...state,
                userEmailStatus: action.payload ? 'success' : '',
                currentUser: { ...state.currentUser, isEmailConfirmed: action.payload },
            }

        case UserActionTypes.START_CONFIRM_USER_EMAIL:
            return {
                ...state,
                userEmailStatus: null,
            }
        case UserActionTypes.SUCCESS_CONFIRM_USER_EMAIL:
            return {
                ...state,
                userEmailStatus: 'success',
                currentUser: action.payload === undefined ? null : { ...state.currentUser, isEmailConfirmed: action.payload },
            }
        case UserActionTypes.FINISH_CONFIRM_USER_EMAIL:
            return {
                ...state,
                userEmailStatus: null,
            }
        case UserActionTypes.FAILURE_CONFIRM_USER_EMAIL:
            return {
                ...state,
                userEmailStatus: 'failure',
            }

        case UserActionTypes.START_SWITCH_USER_ACCOUNT:
            return {
                ...state,
                loader: { ...state.loader, SWITCH_USER_ACCOUNT: true },
            }
        case UserActionTypes.SUCCESS_SWITCH_USER_ACCOUNT:
            return {
                ...state,
                loader: { ...state.loader, SWITCH_USER_ACCOUNT: null },
                success: { ...state.success, SWITCH_USER_ACCOUNT: true },
            }
        case UserActionTypes.FINISH_SWITCH_USER_ACCOUNT:
            return {
                ...state,
                loader: { ...state.loader, SWITCH_USER_ACCOUNT: null },
                success: { ...state.success, SWITCH_USER_ACCOUNT: null },
            }
        case UserActionTypes.FAILURE_SWITCH_USER_ACCOUNT:
            return {
                ...state,
                loader: { ...state.loader, SWITCH_USER_ACCOUNT: null },
                success: { ...state.success, SWITCH_USER_ACCOUNT: null },
            }

        case UserActionTypes.START_SWITCH_ONBOARDING_INTENT:
            return {
                ...state,
                loader: { ...state.loader, SWITCH_ONBOARDING_INTENT: true },
            }
        case UserActionTypes.SUCCESS_SWITCH_ONBOARDING_INTENT:
            return {
                ...state,
                loader: { ...state.loader, SWITCH_ONBOARDING_INTENT: null },
                success: { ...state.success, SWITCH_ONBOARDING_INTENT: true },
            }
        case UserActionTypes.FINISH_SWITCH_ONBOARDING_INTENT:
            return {
                ...state,
                loader: { ...state.loader, SWITCH_ONBOARDING_INTENT: null },
                success: { ...state.success, SWITCH_ONBOARDING_INTENT: null },
            }
        case UserActionTypes.FAILURE_SWITCH_ONBOARDING_INTENT:
            return {
                ...state,
                loader: { ...state.loader, SWITCH_ONBOARDING_INTENT: null },
                success: { ...state.success, SWITCH_ONBOARDING_INTENT: null },
            }

        case UserActionTypes.START_PLAYGROUND_MODE:
            return {
                ...state,
                loader: { ...state.loader, PLAYGROUND_MODE: true },
            }
        case UserActionTypes.SUCCESS_PLAYGROUND_MODE:
            return {
                ...state,
                success: { ...state.success, PLAYGROUND_MODE: true },
                loader: { ...state.loader, PLAYGROUND_MODE: null },
            }
        case UserActionTypes.FINISH_PLAYGROUND_MODE:
            return {
                ...state,
                success: { ...state.success, PLAYGROUND_MODE: null },
                loader: { ...state.loader, PLAYGROUND_MODE: null },
            }

        case UserActionTypes.START_EXTERNAL_ACCOUNT_LINK:
            return {
                ...state,
                loader: { ...state.loader, EXTERNAL_ACCOUNT_LINK: true },
            }
        case UserActionTypes.SUCCESS_EXTERNAL_ACCOUNT_LINK:
            return {
                ...state,
                loader: { ...state.loader, EXTERNAL_ACCOUNT_LINK: null },
                success: { ...state.success, EXTERNAL_ACCOUNT_LINK: true },
                successData: action.payload,
                error: null,
            }
        case UserActionTypes.FINISH_EXTERNAL_ACCOUNT_LINK:
            return {
                ...state,
                loader: { ...state.loader, EXTERNAL_ACCOUNT_LINK: null },
                success: { ...state.success, EXTERNAL_ACCOUNT_LINK: null },
                successData: null,
                error: null,
            }
        case UserActionTypes.FAILURE_EXTERNAL_ACCOUNT_LINK:
            return {
                ...state,
                loader: { ...state.loader, EXTERNAL_ACCOUNT_LINK: null },
                success: { ...state.success, EXTERNAL_ACCOUNT_LINK: null },
                successData: null,
                error: true,
            }

        case UserActionTypes.FETCH_USER_ADDRESSES:
            return {
                ...state,
                loader: { ...state.loader, USER_ADDRESSES: true },
            }
        case UserActionTypes.SUCCESS_USER_ADDRESSES:
            return {
                ...state,
                loader: { ...state.loader, USER_ADDRESSES: null },
                userAddresses: action.payload,
            }
        case UserActionTypes.FINISH_USER_ADDRESSES:
            return {
                ...state,
                loader: { ...state.loader, USER_ADDRESSES: null },
                userAddresses: null,
            }
        case UserActionTypes.FAILURE_USER_ADDRESSES:
            return {
                ...state,
                loader: { ...state.loader, USER_ADDRESSES: null },
                userAddresses: null,
            }

        case UserActionTypes.LOGOUT_START:
            return {
                ...state,
                loader: { ...state.loader, LOGOUT: true },
            }

        case UserActionTypes.LOGOUT_SUCCESS:
            return {
                ...state,
                loader: { ...state.loader, LOGOUT: null },
                error: null,
            }

        case UserActionTypes.LOGOUT_FAILURE:
            return {
                ...state,
                loader: { ...state.loader, LOGOUT: null },
                error: true,
            }

        case UserActionTypes.START_COMPLETE_USER_SS_ACCESS_VERIFICATION:
            return {
                ...state,
                loader: { ...state.loader, COMPLETE_USER_SS_ACCESS_VERIFICATION: true },
            }
        case UserActionTypes.SUCCESS_COMPLETE_USER_SS_ACCESS_VERIFICATION:
            return {
                ...state,
                loader: { ...state.loader, COMPLETE_USER_SS_ACCESS_VERIFICATION: null },
                currentUser: action.payload,
                error: null,
            }
        case UserActionTypes.FAILURE_COMPLETE_USER_SS_ACCESS_VERIFICATION:
            return {
                ...state,
                loader: { ...state.loader, COMPLETE_USER_SS_ACCESS_VERIFICATION: null },
                error: true,
            }

        case UserActionTypes.START_PUT_PERSONAL_QUESTIONNAIRE:
            return {
                ...state,
                loader: { ...state.loader, PUT_PERSONAL_QUESTIONNAIRE: true },
            }
        case UserActionTypes.SUCCESS_PUT_PERSONAL_QUESTIONNAIRE:
            return {
                ...state,
                loader: { ...state.loader, PUT_PERSONAL_QUESTIONNAIRE: null },
                success: { ...state.success, PUT_PERSONAL_QUESTIONNAIRE: true },
                error: null,
            }
        case UserActionTypes.FINISH_PUT_PERSONAL_QUESTIONNAIRE:
            return {
                ...state,
                loader: { ...state.loader, PUT_PERSONAL_QUESTIONNAIRE: null },
                success: { ...state.success, PUT_PERSONAL_QUESTIONNAIRE: null },
                error: null,
            }
        case UserActionTypes.FAILURE_PUT_PERSONAL_QUESTIONNAIRE:
            return {
                ...state,
                loader: { ...state.loader, PUT_PERSONAL_QUESTIONNAIRE: null },
                success: { ...state.success, PUT_PERSONAL_QUESTIONNAIRE: null },
                error: true,
            }

        case UserActionTypes.START_GET_COMPANIES_REGISTRATION_FEE:
            return {
                ...state,
                loader: { ...state.loader, GET_COMPANIES_REGISTRATION_FEE: true },
            }
        case UserActionTypes.SUCCESS_GET_COMPANIES_REGISTRATION_FEE:
            return {
                ...state,
                loader: { ...state.loader, GET_COMPANIES_REGISTRATION_FEE: null },
                companiesRegistrationFee: action.payload,
                error: null,
            }
        case UserActionTypes.FINISH_GET_COMPANIES_REGISTRATION_FEE:
            return {
                ...state,
                loader: { ...state.loader, GET_COMPANIES_REGISTRATION_FEE: null },
                companiesRegistrationFee: null,
                error: null,
            }
        case UserActionTypes.FAILURE_GET_COMPANIES_REGISTRATION_FEE:
            return {
                ...state,
                loader: { ...state.loader, GET_COMPANIES_REGISTRATION_FEE: null },
                companiesRegistrationFee: null,
                error: true,
            }

        case UserActionTypes.START_FETCH_IDENTITY:
            return {
                ...state,
                loader: { ...state.loader, FETCH_IDENTITY: true },
            }
        case UserActionTypes.SUCCESS_FETCH_IDENTITY:
            return {
                ...state,
                loader: { ...state.loader, FETCH_IDENTITY: null },
                canCreateCompany: action.payload.canCreateCompany,
                currentUser: action.payload,
            }

        case UserActionTypes.START_GET_IDENTITY_DEPOSIT_LIMITS:
            return {
                ...state,
                loader: { ...state.loader, GET_IDENTITY_DEPOSIT_LIMITS: true },
            }
        case UserActionTypes.SUCCESS_GET_IDENTITY_DEPOSIT_LIMITS:
            return {
                ...state,
                loader: { ...state.loader, GET_IDENTITY_DEPOSIT_LIMITS: null },
                identityLimits: action.payload,
                error: null,
            }
        case UserActionTypes.FINISH_GET_IDENTITY_DEPOSIT_LIMITS:
            return {
                ...state,
                loader: { ...state.loader, GET_IDENTITY_DEPOSIT_LIMITS: null },
                identityLimits: null,
                error: null,
            }
        case UserActionTypes.FAILURE_GET_IDENTITY_DEPOSIT_LIMITS:
            return {
                ...state,
                loader: { ...state.loader, GET_IDENTITY_DEPOSIT_LIMITS: null },
                identityLimits: null,
                error: true,
            }

        case UserActionTypes.START_REFRESH_TOKEN:
            return {
                ...state,
                loader: { ...state.loader, REFRESH_TOKEN: true },
                error: null,
            }
        case UserActionTypes.SUCCESS_REFRESH_TOKEN:
            return {
                ...state,
                loader: { ...state.loader, REFRESH_TOKEN: null },
                error: null,
            }
        case UserActionTypes.FINISH_REFRESH_TOKEN:
            return {
                ...state,
                loader: { ...state.loader, REFRESH_TOKEN: null },
                error: null,
            }
        case UserActionTypes.FAILURE_REFRESH_TOKEN:
            return {
                ...state,
                loader: { ...state.loader, REFRESH_TOKEN: null },
                error: true,
            }

        case UserActionTypes.START_ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS:
            return {
                ...state,
                loader: { ...state.loader, ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS: true },
            }
        case UserActionTypes.SUCCESS_ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS:
            return {
                ...state,
                loader: { ...state.loader, ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS: null },
                success: { ...state.success, ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS: true },
                currentUser: { ...state.currentUser, isAffiliateTermsAndConditionsAccepted: true },
            }
        case UserActionTypes.FINISH_ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS:
            return {
                ...state,
                loader: { ...state.loader, ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS: null },
                success: { ...state.success, ACCEPT_AFFILIATE_TERMS_AND_CONDITIONS: null },
            }

        case UserActionTypes.START_CHECK_EMAIL_AVAILABILITY:
            return {
                ...state,
                loader: { ...state.loader, CHECK_EMAIL_AVAILABILITY: true },
                emailExists: null,
                error: null,
            }
        case UserActionTypes.SUCCESS_CHECK_EMAIL_AVAILABILITY:
            return {
                ...state,
                loader: { ...state.loader, CHECK_EMAIL_AVAILABILITY: null },
                emailExists: action.payload.emailExists,
                error: null,
            }
        case UserActionTypes.FAILURE_CHECK_EMAIL_AVAILABILITY:
            return {
                ...state,
                loader: { ...state.loader, CHECK_EMAIL_AVAILABILITY: null },
                emailExists: null,
                error: true,
            }

        case UserActionTypes.START_GET_IDENTITY_DEVICES:
            return {
                ...state,
                loader: { ...state.loader, GET_IDENTITY_DEVICES: true },
            }
        case UserActionTypes.SUCCESS_GET_IDENTITY_DEVICES:
            return {
                ...state,
                loader: { ...state.loader, GET_IDENTITY_DEVICES: null },
                success: { ...state.success, GET_IDENTITY_DEVICES: true },
                devices: action.payload.devices,
                error: null,
            }
        case UserActionTypes.FINISH_GET_IDENTITY_DEVICES:
            return {
                ...state,
                loader: { ...state.loader, GET_IDENTITY_DEVICES: null },
                success: { ...state.success, GET_IDENTITY_DEVICES: null },
                devices: null,
                error: null,
            }
        case UserActionTypes.FAILURE_GET_IDENTITY_DEVICES:
            return {
                ...state,
                loader: { ...state.loader, GET_IDENTITY_DEVICES: null },
                success: { ...state.success, GET_IDENTITY_DEVICES: null },
                devices: null,
                error: true,
            }

        case UserActionTypes.START_SIGN_OUT_DEVICE:
            return {
                ...state,
                loader: { ...state.loader, SIGN_OUT_DEVICE: true },
            }
        case UserActionTypes.SUCCESS_SIGN_OUT_DEVICE:
            return {
                ...state,
                loader: { ...state.loader, SIGN_OUT_DEVICE: null },
                success: { ...state.success, SIGN_OUT_DEVICE: true },
                error: null,
            }
        case UserActionTypes.FINISH_SIGN_OUT_DEVICE:
            return {
                ...state,
                loader: { ...state.loader, SIGN_OUT_DEVICE: null },
                success: { ...state.success, SIGN_OUT_DEVICE: null },
                error: null,
            }
        case UserActionTypes.FAILURE_SIGN_OUT_DEVICE:
            return {
                ...state,
                loader: { ...state.loader, SIGN_OUT_DEVICE: null },
                success: { ...state.success, SIGN_OUT_DEVICE: null },
                error: true,
            }

        case UserActionTypes.SET_SUMSUB_DOCUMENTS_SUBMITTED:
            return {
                ...state,
                sumsubDocumentsSubmitted: action.payload,
            }

        default:
            return state
    }
}

const enabledServiceTypes = data => data?.enabledServices.reduce((o, i) => ({ ...o, [i]: true }), {})

const enabledUserPermissions = data => data?.reduce((o, i) => ({ ...o, [i]: true }), {})

export default userReducer
